import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';

export type Language = 'no' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('no');
  const [translations, setTranslations] = useState<{ no: any, en: any } | null>(null);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const [noResponse, enResponse] = await Promise.all([
          fetch('/i18n/locales/no.json'),
          fetch('/i18n/locales/en.json')
        ]);
        const noData = await noResponse.json();
        const enData = await enResponse.json();
        setTranslations({ no: noData, en: enData });
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };
    fetchTranslations();
  }, []);

  const t = useCallback((key: string): string => {
    if (!translations) {
      return key; // Return key if translations are not loaded yet
    }
    const keys = key.split('.');
    let result: any = translations[language];
    for (const k of keys) {
        result = result?.[k];
        if (result === undefined) {
            return key; 
        }
    }
    return result || key;
  }, [language, translations]);
  
  if (!translations) {
    // Render nothing until translations are loaded to prevent FOUC
    return null;
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
